/**
 * MarkdownContent Component
 * Renders markdown content with support for code blocks, tables,
 * thinking blocks, and all standard AI model output formats
 */

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import { memo, useState, type ReactNode } from 'react';
import { Copy, Check } from 'lucide-react';
import { cn } from '@/utils/classNames';

// F-11: narrow rehypeHighlight's auto-registered language set from
// the full highlight.js pack (~190 languages, ~200 KB minified) to the
// short list of languages this app actually renders in fenced blocks.
// The list was pinned from a grep across frontend/src + docs/ for the
// fence tags that appear in tests, components, and AI-generated answers
// the chat is expected to surface. Everything else falls back to plain
// (uncolored) text via `detect: false`.
import javascript from 'highlight.js/lib/languages/javascript';
import typescript from 'highlight.js/lib/languages/typescript';
import python from 'highlight.js/lib/languages/python';
import json from 'highlight.js/lib/languages/json';
import bash from 'highlight.js/lib/languages/bash';
import yaml from 'highlight.js/lib/languages/yaml';
import xml from 'highlight.js/lib/languages/xml';
import css from 'highlight.js/lib/languages/css';
import sql from 'highlight.js/lib/languages/sql';
import markdown from 'highlight.js/lib/languages/markdown';
import diff from 'highlight.js/lib/languages/diff';
import dockerfile from 'highlight.js/lib/languages/dockerfile';
import powershell from 'highlight.js/lib/languages/powershell';
import plaintext from 'highlight.js/lib/languages/plaintext';

// rehype-highlight wraps every syntax token in a <span>, so `children` on a
// fenced code block is a React element tree — not the raw source. Calling
// String() on that produces `[object Object],[object Object],...` which is
// what ends up in the clipboard if it's used as the copy source. Walk the
// tree and extract just the text instead.
function reactNodeToText(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(reactNodeToText).join('');
  if (typeof node === 'object' && 'props' in node) {
    return reactNodeToText(
      (node as { props: { children?: ReactNode } }).props.children
    );
  }
  return '';
}

interface MarkdownContentProps {
  content: string;
  className?: string;
}

export const MarkdownContent = memo(function MarkdownContent({
  content,
  className = '',
}: MarkdownContentProps) {
  // Pre-process content to handle thinking blocks
  const processedContent = processThinkingBlocks(content);

  // Handle empty or whitespace-only content AFTER processing
  if (!content || !processedContent || processedContent.trim().length === 0) {
    return (
      <div className={cn('markdown-content prose prose-slate max-w-none', className)}>
        <p className="text-text-tertiary italic">No content to display</p>
      </div>
    );
  }

  return (
    <div className={cn('markdown-content prose prose-slate max-w-none', className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[
          [
            rehypeHighlight,
            {
              languages: {
                javascript,
                typescript,
                python,
                json,
                bash,
                yaml,
                xml,
                css,
                sql,
                markdown,
                diff,
                dockerfile,
                powershell,
                plaintext,
              },
              detect: false,
              aliases: {
                javascript: ['js', 'jsx', 'mjs', 'cjs'],
                typescript: ['ts', 'tsx'],
                xml: ['html'],
                bash: ['sh', 'shell', 'console'],
                markdown: ['md', 'mdx'],
                plaintext: ['text', 'txt'],
              },
            },
          ],
          rehypeRaw,
        ]}
        components={{
          // Code blocks with syntax highlighting and copy button
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          code(props: any) {
            const { className, children, ...rest } = props;
            const match = /language-(\w+)/.exec(className || '');
            const language = match ? match[1] : '';
            const text = reactNodeToText(children);

            // react-markdown v10 removed the `inline` prop, so we can't rely
            // on it (it's always undefined → everything used to render as a
            // block). Fenced/block code is rendered inside <pre> and either
            // carries a `language-…` class or spans multiple lines; bare
            // inline code (e.g. a `Column Name` in a list) has neither. Treat
            // only those as block code so an inline-code list isn't rendered
            // as a stack of full code boxes.
            const isBlock = Boolean(match) || text.includes('\n');

            if (isBlock) {
              // Block code - wrap with our CodeBlock component.
              // rehype-highlight has already applied syntax coloring; the
              // `code` prop is the plain-text copy source (extracted from
              // the React tree), while `syntaxHighlightedChildren` keeps
              // the rendered colored tokens.
              return (
                <CodeBlock
                  code={text.replace(/\n$/, '')}
                  language={language}
                  syntaxHighlightedChildren={children}
                />
              );
            }

            // Inline code
            return (
              <code
                className={cn(
                  'rounded px-2 py-0.5 mx-0.5',
                  'bg-bg-secondary text-[#24292f] font-mono text-[13px]',
                  'dark:bg-[#343942] dark:text-[#e6edf3]',
                  'border border-border-default dark:border-gray-700'
                )}
                {...rest}
              >
                {children}
              </code>
            );
          },

          // Thinking blocks render as styled blockquotes. The 🤔 prefix
          // is injected upstream by processThinkingBlocks().
          blockquote({ children }) {
            return (
              <blockquote className="border-l-4 border-stellar-primary pl-4 italic text-text-primary dark:text-text-secondary">
                {children}
              </blockquote>
            );
          },

          // Tables with proper styling
          table({ children }) {
            return (
              <div className="overflow-x-auto my-4">
                <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-700">
                  {children}
                </table>
              </div>
            );
          },

          // Lists with proper spacing
          ul({ children }) {
            return <ul className="list-disc list-inside space-y-1 my-2">{children}</ul>;
          },

          ol({ children }) {
            return <ol className="list-decimal list-inside space-y-1 my-2">{children}</ol>;
          },

          // Links with proper styling and security
          a({ href, children }) {
            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-stellar-primary hover:underline"
              >
                {children}
              </a>
            );
          },

          // Headings with proper hierarchy
          h1({ children }) {
            return <h1 className="text-2xl font-bold mt-6 mb-4">{children}</h1>;
          },

          h2({ children }) {
            return <h2 className="text-xl font-bold mt-5 mb-3">{children}</h2>;
          },

          h3({ children }) {
            return <h3 className="text-lg font-semibold mt-4 mb-2">{children}</h3>;
          },

          // Paragraphs with spacing
          p({ children }) {
            return <p className="my-2 leading-relaxed">{children}</p>;
          },
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
});

/**
 * Code Block Component with Copy Button
 * Works with rehype-highlight to show syntax-colored code
 */
interface CodeBlockProps {
  code: string;
  language: string;
  syntaxHighlightedChildren: React.ReactNode;
}

function CodeBlock({ code, language, syntaxHighlightedChildren }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative my-4 rounded-lg border border-gray-700 bg-[#0d1117] dark:bg-[#0d1117] overflow-hidden">
      {/* Language badge and copy button */}
      <div className="flex items-center justify-between px-4 py-2 bg-[#161b22] border-b border-gray-700">
        <span className="text-xs font-semibold text-text-tertiary uppercase tracking-wide">
          {language || 'text'}
        </span>
        <button
          onClick={handleCopy}
          className={cn(
            'flex items-center gap-1.5 rounded-md px-2.5 py-1.5',
            'text-xs font-medium transition-all duration-200',
            copied
              ? 'bg-success text-white'
              : 'bg-gray-700 text-gray-200 hover:bg-gray-600 hover:text-white'
          )}
          aria-label={copied ? 'Copied to clipboard' : 'Copy code to clipboard'}
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5" />
              Copied
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5" />
              Copy code
            </>
          )}
        </button>
      </div>

      {/* Code content with syntax highlighting from rehype-highlight */}
      <pre className="overflow-x-auto p-4 !bg-[#0d1117] !m-0">
        <code className={cn('text-sm font-mono leading-relaxed !bg-transparent', language && `language-${language}`)}>
          {syntaxHighlightedChildren}
        </code>
      </pre>
    </div>
  );
}

/**
 * Process thinking blocks in content
 * Handles various thinking block formats:
 * - <thinking>...</thinking>
 * - [THINKING]...[/THINKING]
 * - <!-- thinking -->...<!-- /thinking -->
 *
 * The transformed output is a regular markdown blockquote prefixed with
 * 🤔, which is then styled by the blockquote renderer above. A previous
 * iteration of this file shipped a collapsible <ThinkingBlock> component,
 * but the renderer's detection branch was unreachable (React passes
 * children as elements, not strings, so the substring check never fired).
 * If a collapsible variant is wanted in the future, add it back as a
 * deliberate UX change rather than reviving the dead branch.
 */
function processThinkingBlocks(content: string): string {
  // Replace <thinking> tags with blockquotes
  let processed = content.replace(
    /<thinking>([\s\S]*?)<\/thinking>/gi,
    (_, thinking) => `> 🤔 **Thinking Process**\n>\n${thinking.split('\n').map((line: string) => `> ${line}`).join('\n')}`
  );

  // Replace [THINKING] tags
  processed = processed.replace(
    /\[THINKING\]([\s\S]*?)\[\/THINKING\]/gi,
    (_, thinking) => `> 🤔 **Thinking Process**\n>\n${thinking.split('\n').map((line: string) => `> ${line}`).join('\n')}`
  );

  // Replace HTML comment style thinking blocks
  processed = processed.replace(
    /<!--\s*thinking\s*-->([\s\S]*?)<!--\s*\/thinking\s*-->/gi,
    (_, thinking) => `> 🤔 **Thinking Process**\n>\n${thinking.split('\n').map((line: string) => `> ${line}`).join('\n')}`
  );

  return processed;
}
